import { useEffect, useRef, useState } from "react";

// F139 (founder item 9, 11 Oct 2026) — THE DESK MOVES BY ITSELF.
//
// WHAT THE LIVE CHANNEL IS: there is no socket and no SSE. The desk's live channel is a re-fetch of
// GET /owner/desk on a timer, and the whole row + the portfolio totals come from that one payload — so
// anything that re-fetches moves the row AND the totals together.
//
// WHY IT DID NOT FIRE (prod RCA, Crenshaw bld-cda6e1ce1610 + Bammel bld-b77a3a1a8883):
//   1. the old predicate only treated `working` / `reading` / `upstream_incomplete` as in flight. Both
//      buildings' FIRST mark landed fast (48.8s / 21.7s) but landed as a HOLD — needs_owner_class,
//      flag classification_incomplete_hold — which the projection renders as `waiting_on_you`. Not in
//      the predicate, so the timer stopped while the cell still showed the old "Reading…"/"Working".
//   2. the budget was 45 ticks × 5s = 3m45s, counted ONCE per mount and never reset. Bammel's second
//      mark (the owner_answer re-mark) landed at 4m16.9s — past the ceiling — so even a correct
//      predicate would have given up before it.
// So: every state the AGENTS can leave by themselves is in flight, the fast lane's clock RESTARTS on
// every genuine state change, and when nothing is in flight a slow heartbeat keeps running, which is
// what carries handoff → working (a person acts, not this browser) without a reload.

export const FAST_MS = 5000;        // something is in flight: the agents are moving
export const SLOW_MS = 20000;       // heartbeat: nothing in flight here, but a person may act anywhere
export const FAST_WINDOW_MS = 20 * 60 * 1000;  // how long the fast lane runs without a single change

// Every state the agents/staff can move out of with no help from this browser.
const IN_FLIGHT_EMPTY = new Set([
  "reading",               // the Librarian has the file
  "upstream_incomplete",   // filed, no mark yet
  "working",               // a recompute is queued or running
  "waiting_on_you",        // a hold the owner answers — and the answer's re-mark lands here too
  "needs_a_person",        // handed to our team; they resolve it, not this tab
]);

export function markRung(row) {
  return (row && row.rungs && row.rungs.mark) || {};
}

export function deskPending(desk) {
  return ((desk && desk.book && desk.book.rows) || []).some((r) => {
    const m = markRung(r);
    return m.working === true || m.re_reading === true || IN_FLIGHT_EMPTY.has(m.empty);
  });
}

// What the desk LOOKS like right now, as one string. A different signature is a real change — it is
// what restarts the fast lane's clock, so a long read never runs out of budget mid-flight.
export function deskSignature(desk) {
  const rows = ((desk && desk.book && desk.book.rows) || []).map((r) => {
    const m = markRung(r);
    return [r.building_ref, m.state, m.empty, m.value, m.re_reading ? 1 : 0, m.message].join("~");
  });
  const s = (desk && desk.book && desk.book.summary) || {};
  return [...rows, s.portfolio_value, s.marked_count, (desk && desk.decisions_count)].join("|");
}

export function nextPollDelay({ pending, msSinceChange, visible }) {
  if (!visible) return null;                       // F44: never poll a hidden tab
  if (pending && msSinceChange < FAST_WINDOW_MS) return FAST_MS;
  return SLOW_MS;                                  // a heartbeat, always — never "stopped for good"
}

/** Re-fetch the desk on its own, for ever, at the right cadence. `load` must be stable. */
export function useLiveDesk(desk, load) {
  const [visible, setVisible] = useState(typeof document === "undefined" || !document.hidden);
  const sig = useRef(null);
  const changedAt = useRef(Date.now());

  useEffect(() => {
    const onVis = () => {
      const now = !document.hidden;
      setVisible(now);
      if (now) load();                             // come back to the tab and the desk is current
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  const current = deskSignature(desk);
  if (sig.current !== current) {
    sig.current = current;
    changedAt.current = Date.now();
  }

  useEffect(() => {
    if (!desk) return undefined;
    const delay = nextPollDelay({ pending: deskPending(desk),
                                  msSinceChange: Date.now() - changedAt.current, visible });
    if (delay === null) return undefined;
    const t = setTimeout(load, delay);
    return () => clearTimeout(t);
  }, [desk, load, visible]);

  return { live: visible && deskPending(desk) };
}
